import { useMemo, useRef, useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { useProperty } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup, EditableToken, useContentToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { formatBedroomsList } from '../utils/formatBedroomsList';

/**
 * Capitalize the first textual character of an HTML fragment, skipping
 * any leading tags or whitespace. Leaves the string unchanged if the
 * first non-whitespace character is a digit (per the "if not a number"
 * rule). Applied at render time so it works for both the schema default
 * and any value an author types into the inline editor.
 */
function capitalizeFirstChar(html: string): string {
  if (!html) return html;
  let inTag = false;
  for (let i = 0; i < html.length; i++) {
    const c = html[i];
    if (inTag) {
      if (c === '>') inTag = false;
      continue;
    }
    if (c === '<') {
      inTag = true;
      continue;
    }
    if (/\s/.test(c)) continue;
    // First real character — bail if it's a digit, otherwise uppercase it.
    if (/\d/.test(c)) return html;
    const upper = c.toUpperCase();
    if (upper === c) return html;
    return html.slice(0, i) + upper + html.slice(i + 1);
  }
  return html;
}

/**
 * Lists out a raw bedrooms range without any "Bedroom" suffix, so it can be
 * substituted back into a field token ("1 - 3" -> "1, 2 & 3"). Returns the
 * original value unchanged when it can't be parsed.
 */
function listBedroomsRange(raw: string | null | undefined): string | null | undefined {
  if (!raw) return raw;
  return formatBedroomsList(String(raw)) || raw;
}

/**
 * Formats a raw bedrooms range into natural copy with every count listed:
 * "1" → "1 Bedroom", "1 - 3" → "1, 2 & 3 Bedroom",
 * "studio - 2" → "Studio, 1 & 2 Bedroom", "studio" → "Studio".
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const label = formatBedroomsList(raw);
  if (!label) return 'Apartments';
  return label === 'Studio' ? label : `${label} Bedroom`;
}

/**
 * Keep multi-word city names (and optional ", ST") from wrapping mid-name
 * by wrapping the location in a nowrap span. Safe to call on already-
 * resolved subtitle HTML — skips when the city is already protected.
 */
function keepLocationTogether(
  html: string,
  city: string | null | undefined,
  state: string | null | undefined,
): string {
  const cityStr = (city && String(city).trim()) || '';
  if (!cityStr || !html.includes(cityStr)) return html;

  const stateStr = (state && String(state).trim()) || '';
  const location = stateStr ? `${cityStr}, ${stateStr}` : cityStr;
  const target = html.includes(location) ? location : cityStr;

  const cityEscaped = cityStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`white-space:\\s*nowrap[^>]*>[^<]*${cityEscaped}`).test(html)) {
    return html;
  }

  const targetEscaped = target.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return html.replace(
    new RegExp(targetEscaped, 'g'),
    `<span style="white-space:nowrap">${target}</span>`,
  );
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  const property = useProperty();

  const featuredVideoRaw = (property as Record<string, unknown>)['custom_property_featured_video'] as string | null | undefined;

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const hasVideo = !!(featuredVideoRaw && typeof featuredVideoRaw === 'string' && featuredVideoRaw.trim());

  const videoInfo = useMemo(() => {
    if (!hasVideo) return null;
    const url = featuredVideoRaw!.trim();

    // Vimeo embed or watch URL
    if (url.includes('vimeo.com')) {
      const videoIdMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      const videoId = videoIdMatch?.[1];
      if (!videoId) return { type: 'embed' as const, src: url };
      return {
        type: 'embed' as const,
        src: `https://player.vimeo.com/video/${videoId}?autoplay=1&loop=1&title=0&byline=0&portrait=0&background=1&muted=1&autopause=0`,
      };
    }

    // YouTube embed or watch URL
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId: string | null = null;
      const embedMatch = url.match(/embed\/([^?&/]+)/);
      const watchMatch = url.match(/[?&]v=([^&]+)/);
      const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
      videoId = embedMatch?.[1] || watchMatch?.[1] || shortMatch?.[1] || null;
      if (!videoId) return { type: 'embed' as const, src: url };
      return {
        type: 'embed' as const,
        src: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1`,
      };
    }

    // Direct video file URL (.mp4, .webm, etc.)
    return { type: 'direct' as const, src: url };
  }, [hasVideo, featuredVideoRaw]);

  useEffect(() => {
    if (!videoInfo || videoInfo.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, [videoInfo]);

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = (city && String(city).trim()) || '';
    const stateStr = (state && String(state).trim()) || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    const locationHtml = location
      ? ` in <span style="white-space:nowrap">${location}</span>`
      : '';
    return `<p>${beds} Apartments for Rent${locationHtml}</p>`;
  }, [bedroomsRange, city, state]);

  // Render the subtitle through an external transform so the first character
  // is capitalized (unless it's a number) regardless of what the author types.
  // We replicate the minimal RichTextContent pipeline here (resolve field
  // tokens, strip a redundant outer <p> since EditableToken wraps in <p>)
  // and then apply capitalizeFirstChar to the resolved HTML.
  const subtitleRaw = useContentToken('hero.subtitle', { defaultValue: subtitleHtml });
  // A stored subtitle may embed the property_bedrooms_range field token, which
  // resolveRichText substitutes with the raw value ("1 - 3"). List out the
  // field value first so the resolved copy reads "1, 2 & 3 Bedroom".
  const resolveProperty = useMemo(() => ({
    ...(property as Record<string, any>),
    property_bedrooms_range: listBedroomsRange(bedroomsRange as string | null),
  }), [property, bedroomsRange]);
  const subtitleResolved = useMemo(() => {
    if (!subtitleRaw) return '';
    let html = resolveRichText(subtitleRaw, resolveProperty);
    const trimmed = html.trim();
    const wrap = trimmed.match(/^<p>([\s\S]*)<\/p>$/);
    if (wrap && !/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(wrap[1])) {
      html = wrap[1];
    }
    return keepLocationTogether(
      capitalizeFirstChar(html),
      city as string | null,
      state as string | null,
    );
  }, [subtitleRaw, resolveProperty, city, state]);

  return (
    <section className="relative w-full h-[60vh] md:h-[68vh] min-h-[360px] max-h-[750px] overflow-hidden">
      {/* Background — video if available, otherwise Ken Burns image */}
      {videoInfo ? (
        <div className="absolute inset-0">
          {/* Fallback image shows while video loads */}
          {bgImage.image && (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                videoInfo.type === 'embed' ? (iframeLoaded ? 'opacity-0' : 'opacity-100') :
                (videoReady ? 'opacity-0' : 'opacity-100')
              }`}
            />
          )}
          {videoInfo.type === 'embed' ? (
            <iframe
              src={videoInfo.src}
              className={`hero-video-iframe absolute inset-0 w-full h-full transition-opacity duration-1000 ${iframeLoaded ? 'opacity-100' : 'opacity-0'}`}
              allow="autoplay; fullscreen"
              frameBorder="0"
              onLoad={() => setIframeLoaded(true)}
              title="Background video"
            />
          ) : (
            <video
              ref={videoRef}
              src={videoInfo.src}
              autoPlay
              muted
              loop
              playsInline
              className={`w-full h-full object-cover transition-opacity duration-1000 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
            />
          )}
        </div>
      ) : (
        <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef} className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              className="w-full h-full object-cover"
              style={{
                animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
                imageRendering: 'high-quality' as any,
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </EditableSlotGroup>
      )}

      {/* Localized shading only behind the header logo (top-left) and the centered copy/CTA */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: [
            'radial-gradient(ellipse 420px 220px at 0% 0%, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.2) 45%, transparent 100%)',
            'radial-gradient(ellipse 55% 45% at 50% 50%, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.18) 50%, transparent 100%)',
          ].join(', '),
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8">
        {/* Accent lines flanking subtitle */}
        <div className="hero-entrance hero-entrance-1 flex items-center gap-4 sm:gap-6 mb-5 max-w-5xl w-full">
          <div className="flex-1 hidden sm:flex items-center min-w-[40px]">
            <svg className="hero-accent-line w-full" style={{ height: '2px' }} viewBox="0 0 200 2" preserveAspectRatio="none">
              <line x1="200" y1="1" x2="0" y2="1" stroke="white" strokeOpacity="0.4" strokeWidth="1.5" style={{ strokeDasharray: 200, strokeDashoffset: 200, animation: 'heroLineDraw 1.4s cubic-bezier(0.22,1,0.36,1) 0.6s forwards' }} />
            </svg>
          </div>

          <EditableToken
            path="hero.subtitle"
            type="richtext"
            label="Subtitle"
            defaultValue={subtitleHtml}
            as="p"
            className="font-body text-sm sm:text-xl md:text-2xl leading-relaxed opacity-90 drop-shadow-md min-w-0 text-center lg:whitespace-nowrap text-[var(--vibe-color-textOnPrimary)]"
          >
            <span
              data-vibesite-richtext=""
              dangerouslySetInnerHTML={{ __html: subtitleResolved }}
              style={{ display: 'contents' }}
            />
          </EditableToken>

          <div className="flex-1 hidden sm:flex items-center min-w-[40px]">
            <svg className="hero-accent-line w-full" style={{ height: '2px' }} viewBox="0 0 200 2" preserveAspectRatio="none">
              <line x1="0" y1="1" x2="200" y2="1" stroke="white" strokeOpacity="0.4" strokeWidth="1.5" style={{ strokeDasharray: 200, strokeDashoffset: 200, animation: 'heroLineDraw 1.4s cubic-bezier(0.22,1,0.36,1) 0.6s forwards' }} />
            </svg>
          </div>
        </div>

        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal mb-10 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg text-[var(--vibe-color-textOnPrimary)]"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-secondary)] overflow-hidden transition-all duration-300"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
            <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        /* Vimeo/YouTube iframe — scale up to fill and cover like object-fit:cover */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw; /* 16:9 */
          min-height: 100%;
          min-width: 177.78vh; /* 100 / (9/16) */
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.2s; }
        .hero-entrance-2 { animation-delay: 0.45s; }
        .hero-entrance-3 { animation-delay: 0.7s; }
        .hero-cta {
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%);
          border-radius: 9999px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .hero-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-secondary) 90%, white) 0%, var(--vibe-color-secondary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-secondary) 40%, transparent);
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          left: 100%;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        @keyframes heroLineDraw {
          to { stroke-dashoffset: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-accent-line line { stroke-dashoffset: 0; animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
